import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import type { ReleaseNote } from '@/types';

import useAuth from '@/hooks/use-auth';
import useBreakpoint from '@/hooks/use-break-point';
import useNewReleaseToastSeen from '@/hooks/use-new-release-toast-seen';
import useSocketSubscription from '@/hooks/use-socket-subscription';
import { cn } from '@/lib/utils';
import useReleaseNoteStore from '@/store/useReleaseNoteStore';
import showReleaseToast from '@/utils/helpers/show-release-toast';

const ReleaseNotificationToaster = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isMobile = useBreakpoint('mobile');
  const isTablet = useBreakpoint('tablet');

  const { openReleaseNotes, setReleaseToastVisible, isAppLoading } = useReleaseNoteStore();
  const { mutate: markReleaseToastSeen } = useNewReleaseToastSeen();
  const { pathname } = useLocation();

  const isControlPanel = pathname === '/control-panel';

  const lastSeenReleaseIdRef = useRef<string | null>(null);

  const { data: release } = useSocketSubscription<ReleaseNote>('latest_release_notification', {
    enabled: isAuthenticated,
    filter: (incoming) => Boolean(incoming?.id),
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.dismiss();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!release?.id || isAppLoading || lastSeenReleaseIdRef.current === release.id) {
      return;
    }

    const releaseId = release.id;
    const toastId = `release-notification-${releaseId}`;

    lastSeenReleaseIdRef.current = releaseId;

    setReleaseToastVisible(true);

    showReleaseToast({
      id: toastId,
      title: t('errors.cache.toaster.title'),
      description: release.version,
      className: cn('mb-11', {
        'mb-5': isControlPanel,
        'mb-7': isMobile,
      }),
      contentClassName: cn({ 'min-w-dvw rounded-none': isMobile, 'flex-start': isTablet }),
      descriptionClassName: 'text-subtext max-w-76.5',
      isMobile,
      duration: Infinity,
      position: isMobile ? 'bottom-center' : 'bottom-left',
      actionLabel: t('actions.view_details'),
      onAction: () => {
        openReleaseNotes();
        markReleaseToastSeen(releaseId);
        toast.dismiss(toastId);
      },
      onDismiss: () => {
        setReleaseToastVisible(false);
        markReleaseToastSeen(releaseId);
      },
    });
  }, [release, isAppLoading, isControlPanel, isMobile, isTablet]);

  return null;
};

export default ReleaseNotificationToaster;
